import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { Loader2 } from 'lucide-react';
import { CockpitTabs } from './taCockpit/cockpitTabs';
import { fetchTACockpitData } from '../services/taCockpitApi';
import type { TACockpitData } from '../types/taCockpit';
import { useAuth } from '../../../contexts/AuthContext';
import { PlanningTab } from './taCockpit/PlanningTab';
import { TrackingSection } from './taCockpit/tracking/TrackingSection';
import { RolePrioritisationTab } from './taCockpit/rolePrioritisation/RolePrioritisationTab';
import { BottleneckRadarTab } from './taCockpit/bottleneck/BottleneckRadarTab';
import { RolePrioritisationProvider } from './taCockpit/rolePrioritisation/RolePrioritisationProvider';
import { currentTrimester } from './taCockpit/trimester';

/**
 * Reading order is Planning → Tracking → Role Prioritisation → Pipeline Radar: plan the trimester,
 * watch it, price what it costs, then diagnose the individual role that is not moving. Role
 * Prioritisation still OPENS first, because it is the question asked most often — the tab order is
 * about how the work reads, not about what you land on.
 */
type Tab = 'planning' | 'tracking' | 'role-prioritisation' | 'pipeline-radar';

const TABS = [
  { value: 'planning', label: 'Planning' },
  { value: 'tracking', label: 'Tracking' },
  { value: 'role-prioritisation', label: 'Role Prioritisation' },
  { value: 'pipeline-radar', label: 'Pipeline Radar' },
] as const satisfies readonly { value: Tab; label: string }[];

const DEFAULT_TAB: Tab = 'role-prioritisation';
const TAB_VALUES = new Set<string>(TABS.map((t) => t.value));

export default function TACockpitView() {
  const { tab: tabParam, subtab: subtabParam } = useParams<{
    tab?: string;
    subtab?: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<TACockpitData | null>(null);
  const [loading, setLoading] = useState(true);

  /*
   * ⚠️ A SHEET FAILURE IS NO LONGER AN ERROR ANYWHERE IN THE COCKPIT.
   *
   * Role Prioritisation never read it. Tracking moved onto the Hiring Database.
   * Capacity planning now reads the HDB too and uses the sheet only to SEED the
   * team's FTE and hires-per-month — for which it has its own last-known values
   * and its own notice. So the sheet is fetched, and a failure simply means
   * `data` stays null; nothing is gated on it and nothing reports it as broken.
   */
  useEffect(() => {
    let cancelled = false;
    fetchTACockpitData()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        /* the capacity tab says so itself, where it is the only thing affected */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Bare `/ta-cockpit` or an unknown `:tab` slug resolves to the default tab canonically, so a
   * bookmark still lands somewhere and the URL matches the tab that renders. The redirect is a
   * one-hop `Navigate replace`, same pattern the TA Action Boards routes use.
   */
  if (!tabParam) {
    return <Navigate to={`/ta-cockpit/${DEFAULT_TAB}`} replace />;
  }
  if (!TAB_VALUES.has(tabParam)) {
    return <Navigate to={`/ta-cockpit/${DEFAULT_TAB}`} replace />;
  }
  const tab = tabParam as Tab;

  function onTabChange(next: Tab) {
    // The Cockpit emitted nothing at all until now, so the usage board could say it was opened and
    // nothing about which of its four sections earns its place. This is that, and it is the
    // cheapest half — what people DO inside a section still needs its own events.
    void logAnalyticsEvent('ta_cockpit_tab_changed', { tab: next });
    // Sub-tab is scoped to the tab it belongs to; leaving Role Prioritisation drops it.
    navigate(`/ta-cockpit/${next}`);
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading TA Cockpit…
      </div>
    );
  }

  const period = currentTrimester().key;

  return (
    <div className="mx-auto max-w-6xl space-y-4 px-6 pb-6 pt-4">
      {/*
        Unconditional: the period comes from the CALENDAR now (trimester.ts), not
        from a cell in the sheet, so a sheet outage no longer makes it unknowable.
        The fallback said "Trimester period unavailable" when it was perfectly
        available, which is a worse failure than the one it was written for.
      */}
      {/*
        ⚠️ ONE ROW: title, period and the section tabs (Theresa, Sep 29 2026). Stacked, they took
        ~110px above every tab's figures before any sub-tab bar was even drawn. Wraps under the
        title on a narrow screen rather than squeezing the tabs.
      */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <Header />
        <CockpitTabs value={tab} tabs={TABS} onChange={onTabChange} ariaLabel="Cockpit sections" />
      </div>

      {/*
        ⚠️ THE PROVIDER WRAPS THE WHOLE TAB BODY, not just Role Prioritisation. The prioritisation
        state is read on three tabs since Sep 25 2026 — the two Role Prioritisation sub-tabs and
        Planning, where the effort model is now edited — and one instance above the strip is what
        makes a tab switch a render rather than a reload through localStorage. See
        `RolePrioritisationProvider` for what that fixed and what it costs.
      */}
      <RolePrioritisationProvider userEmail={user?.email}>
        {tab === 'role-prioritisation' ? (
          <RolePrioritisationTab userEmail={user?.email} subtabSlug={subtabParam} />
        ) : tab === 'tracking' ? (
          <TrackingSection period={period} subtabSlug={subtabParam} />
        ) : tab === 'pipeline-radar' ? (
          <BottleneckRadarTab />
        ) : (
          <PlanningTab data={data} />
        )}
      </RolePrioritisationProvider>
    </div>
  );
}

function Header() {
  // Derived from TODAY, never from the sheet — see views/taCockpit/trimester.ts.
  const period = useMemo(() => currentTrimester().label, []);

  return (
    <div className="flex flex-wrap items-baseline gap-x-3">
      <h1 className="text-xl font-semibold text-gray-900">TA Cockpit</h1>
      <p className="text-sm text-gray-500">{period}</p>
    </div>
  );
}
